import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageBody, PageHeader } from "../components/Layout";
import EmptyState from "../components/EmptyState";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Cell,
} from "recharts";

const SERIES_COLORS = ["#002FA7", "#E63946", "#1f4068", "#0EA5E9", "#9333EA", "#16A34A", "#F59E0B", "#475569"];

function ChartCard({ overline, title, description, children, testid }) {
    return (
        <section data-testid={testid} className="border border-border bg-card p-6">
            <div className="overline mb-1.5">{overline}</div>
            <h3 className="font-display font-semibold text-lg leading-tight">{title}</h3>
            {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
            <div className="mt-5">{children}</div>
        </section>
    );
}

export default function Trends() {
    const [themes, setThemes] = useState(null);
    const [phrases, setPhrases] = useState([]);
    const [emerging, setEmerging] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [topics, setTopics] = useState([]);

    useEffect(() => {
        api.get("/trends/themes", { params: { bucket: "week" } }).then((r) => setThemes(r.data));
        api.get("/trends/phrases").then((r) => setPhrases(r.data.phrases));
        api.get("/trends/emerging-language").then((r) => setEmerging(r.data.terms));
        api.get("/trends/platforms").then((r) => setPlatforms(r.data.platforms));
        api.get("/trends/topics").then((r) => setTopics(r.data.topics));
    }, []);

    const buckets = themes?.buckets || [];
    const series = themes?.series || [];

    // Pivot for line chart
    const lineData = buckets.map((b) => {
        const row = { bucket: b };
        series.forEach((s) => {
            const point = (s.points || []).find((p) => p.label === b);
            row[s.name] = point ? point.value : 0;
        });
        return row;
    });

    const empty = lineData.length === 0 && phrases.length === 0 && platforms.length === 0;

    return (
        <>
            <PageHeader
                overline="Cultural Movement"
                title="Trends"
                description="What's rising. What's repeating. What's quietly shifting in the language people use about dating."
                testid="trends-header"
            />
            <PageBody>
                {empty ? (
                    <EmptyState title="Nothing trending yet" description="Run a few analyses across different sources and dates — the charts populate from there." testid="trends-empty" />
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <ChartCard overline="Theme frequency · weekly" title="Emotional Themes Over Time" testid="theme-trend-chart">
                            {lineData.length === 0 ? (
                                <p className="text-sm text-muted-foreground italic">Need at least 2 analyses across time.</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <LineChart data={lineData} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
                                        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="bucket" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} allowDecimals={false} />
                                        <Tooltip contentStyle={{ backgroundColor: "hsl(var(--popover))", borderColor: "hsl(var(--border))", fontSize: 12 }} />
                                        {series.map((s, i) => (
                                            <Line
                                                key={s.name}
                                                type="monotone"
                                                dataKey={s.name}
                                                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                                                strokeWidth={2}
                                                dot={false}
                                            />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </ChartCard>

                        <ChartCard overline="Verbatim language" title="Top Repeated Phrases" testid="phrases-chart">
                            {phrases.length === 0 ? (
                                <p className="text-sm text-muted-foreground italic">No phrases tracked yet.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {phrases.map((p, i) => (
                                        <li key={i} className="flex items-center justify-between text-sm border-l-2 border-border pl-3">
                                            <span>"{p.phrase}"</span>
                                            <span className="font-mono text-[11px] text-muted-foreground">×{p.count}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </ChartCard>

                        <ChartCard overline="Words gaining traction" title="Emerging Language" testid="emerging-chart">
                            {emerging.length === 0 ? (
                                <p className="text-sm text-muted-foreground italic">No emerging terms yet.</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={emerging} margin={{ top: 5, right: 12, left: 0, bottom: 5 }}>
                                        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="term" stroke="hsl(var(--muted-foreground))" fontSize={10} interval={0} angle={-15} textAnchor="end" height={60} />
                                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} allowDecimals={false} />
                                        <Tooltip contentStyle={{ backgroundColor: "hsl(var(--popover))", borderColor: "hsl(var(--border))", fontSize: 12 }} />
                                        <Bar dataKey="count" fill="hsl(var(--primary))" />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </ChartCard>

                        <ChartCard overline="Comparison" title="Comments by Platform" testid="platform-chart">
                            {platforms.length === 0 ? (
                                <p className="text-sm text-muted-foreground italic">No platforms yet.</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={platforms} layout="vertical" margin={{ top: 5, right: 12, left: 30, bottom: 5 }}>
                                        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" horizontal={false} />
                                        <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={10} allowDecimals={false} />
                                        <YAxis type="category" dataKey="platform" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                                        <Tooltip contentStyle={{ backgroundColor: "hsl(var(--popover))", borderColor: "hsl(var(--border))", fontSize: 12 }} />
                                        <Bar dataKey="count">
                                            {platforms.map((_, i) => (
                                                <Cell key={i} fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </ChartCard>

                        <ChartCard overline="What's tagged most" title="Top Topics" testid="topics-chart">
                            {topics.length === 0 ? (
                                <p className="text-sm text-muted-foreground italic">No tagged topics yet.</p>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {topics.map((t) => (
                                        <span key={t.topic} className="px-3 py-1 border border-border text-xs font-mono rounded-sm">
                                            {t.topic} <span className="text-muted-foreground">×{t.count}</span>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </ChartCard>
                    </div>
                )}
            </PageBody>
        </>
    );
}
